import { useEffect, type ReactNode } from 'react';

/**
 * The report's drill modal — "All keywords" / "All SKUs".
 *
 * Ported from modal() + openDrill() in
 * ops/report-nightly/pages/marketing.html. A card shows the top five rows and
 * this shows every one of them, because a top-five table with no way to see the
 * rest is a table that quietly decides what matters.
 *
 * Not in ds/ because ds owns the modal's LOOK (.modal, .modal-head,
 * .modal-body, .modal-foot are all in ds.css) and this owns its BEHAVIOUR —
 * Escape, scrim click, and locking the page behind it.
 */
export function Drill({
  title, count, search, onSearchChange, foot, onClose, children,
}: {
  title: string;
  /** Sits beside the title: how many rows, over what window. */
  count: string;
  /** Present only on drills the report gives a search box. */
  search?: string;
  onSearchChange?: (q: string) => void;
  foot: ReactNode;
  onClose: () => void;
  children: ReactNode;
}) {
  // The scroll lock belongs to the modal's lifetime, not to the click that
  // opened it: unmounting by any route (Escape, scrim, a re-render upstream)
  // has to give the page its scroll back.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <div
      className="scrim"
      // mousedown, not click: a drag that starts inside the modal and ends on
      // the scrim is not a dismissal.
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="modal">
        <div className="modal-head">
          <span className="modal-title">{title}</span>
          <span className="modal-count">{count}</span>
          <span className="spacer" style={{ flex: 1 }} />
          {onSearchChange ? (
            <input
              className="modal-search"
              type="search"
              placeholder="Search…"
              value={search ?? ''}
              onChange={(e) => { onSearchChange(e.target.value); }}
            />
          ) : null}
          <button
            className="icon-btn"
            aria-label="Close"
            style={{ width: 34, height: 34, flex: '0 0 34px' }}
            onClick={onClose}
          >
            <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
              <path d="M3 3l8 8M11 3l-8 8" />
            </svg>
          </button>
        </div>
        <div className="modal-body">{children}</div>
        <div className="modal-foot">{foot}</div>
      </div>
    </div>
  );
}
